import React, { useState, Fragment } from "react";
import { useDispatch } from "react-redux";

import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormHelperText from "@material-ui/core/FormHelperText";

import Axios from "../../utils/axios";
import { ADM_AREAS_CHILD_LIST_URL } from "../../config";
import { showErrorAlert } from "../../store/actions/alerts";

import CountrySelector from "../adm-areas/CountrySelector";

const AdmAutoComplete = ({
  adm,
  label,
  loading,
  onChange,
  error,
  helperText,
  ...rest
}) => {
  return (
    <Autocomplete
      getOptionLabel={(option) => option.name}
      options={adm}
      loading={loading}
      onChange={onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      {...rest}
    />
  );
};

export default function AdmSelector({
  disabled,
  field,
  form,
  error,
  helperText,
  label,
}) {
  const dispatch = useDispatch();
  const [adm1, setAdm1] = useState([]);
  const [adm2, setAdm2] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAdm, setSelectedAdm] = useState(null);
  const [selCountry, setSelCountry] = useState(null);
  const [selAdm1, setSelAdm1] = useState(null);
  const [selAdm2, setSelAdm2] = useState(null);

  async function handleCountryChange(e, value) {
    setSelCountry(value);
    setSelAdm1(null);
    setSelAdm2(null);
    setAdm1([]);
    setAdm2([]);

    if (value) {
      if (!!value.hasChild) {
        setLoading(true);
        const axiosInstance = new Axios();
        await axiosInstance
          .request({
            url: `${ADM_AREAS_CHILD_LIST_URL.url}/${value.admId}`,
            method: ADM_AREAS_CHILD_LIST_URL.method,
          })
          .then((response) => {
            if (response.data.length > 0) {
              setAdm1(response.data);
            } else {
              dispatch(
                showErrorAlert("No Sub-Administrative Area Found. Please Check")
              );
            }
          })
          .catch((error) => {
            dispatch(
              showErrorAlert(
                "Adm Area 1 Fetch Error : " +
                  (!!error.response
                    ? `${error.response.status}! - ${error.response.data.errorMessage}`
                    : error.message)
              )
            );
          });
        setLoading(false);
      } else {
        setSelectedAdm(value.admId);
        form.setFieldValue(field.name, value.admId);
      }
    }
  }

  async function handleAdm1Change(e, value) {
    console.log(value);
    setSelAdm1(value);
    setSelAdm2(null);
    setAdm2([]);

    if (value) {
      if (!!value.hasChild) {
        setLoading(true);
        const axiosInstance = new Axios();
        await axiosInstance
          .request({
            url: `${ADM_AREAS_CHILD_LIST_URL.url}/${value.admId}`,
            method: ADM_AREAS_CHILD_LIST_URL.method,
          })
          .then((response) => {
            if (response.data.length > 0) {
              setAdm2(response.data);
            } else {
              dispatch(
                showErrorAlert("No Sub-Administrative Area Found. Please Check")
              );
            }
          })
          .catch((error) => {
            dispatch(
              showErrorAlert(
                "Adm Area 1 Fetch Error : " +
                  (!!error.response
                    ? `${error.response.status}! - ${error.response.data.errorMessage}`
                    : error.message)
              )
            );
          });
        setLoading(false);
      } else {
        setSelectedAdm(value.admId);
        form.setFieldValue(field.name, value.admId);
      }
    }
  }

  async function handleAdm2Change(e, value) {
    setSelAdm2(value);
    setSelectedAdm(value.admId);
    form.setFieldValue(field.name, value.admId);
  }

  const onAdmDelete = (type) => {
    setSelectedAdm(null);
    form.setFieldValue(field.name, null);
    switch (type) {
      case 0:
        setSelCountry(null);
        setAdm1([]);
        setAdm2([]);
        break;
      case 1:
        setSelAdm1(null);
        setAdm2([]);
        break;
      case 2:
        setSelAdm2(null);
        break;

      default:
        break;
    }
  };

  return (
    <Box>
      <Typography
        variant="caption"
        display="block"
        style={{ marginTop: "15px" }}
        gutterBottom
      >
        {label}
      </Typography>
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        justifyContent="flex-start"
        alignItems="end"
      >
        <CountrySelector
          onChange={handleCountryChange}
          style={{
            margin: 5,
            width: 300,
            ...(selCountry && { display: "none" }),
          }}
          disabled={disabled}
        />
        {selCountry && (
          <Fragment>
            <Chip
              label={selCountry.name}
              variant="outlined"
              onDelete={() => onAdmDelete(0)}
              style={{
                margin: 5,
                ...(selectedAdm && { backgroundColor: "#008000" }),
              }}
              disabled={disabled}
            />
            {!!selCountry.hasChild && (
              <AdmAutoComplete
                label="Select Adm Area 1"
                adm={adm1}
                loading={loading}
                onChange={handleAdm1Change}
                style={{
                  margin: 5,
                  width: 300,
                  ...(selAdm1 && { display: "none" }),
                }}
                disabled={disabled}
              />
            )}
            {selAdm1 && (
              <Fragment>
                <Chip
                  label={selAdm1.name}
                  onDelete={() => onAdmDelete(1)}
                  style={{
                    margin: 5,
                    ...(selectedAdm && { backgroundColor: "#008000" }),
                  }}
                  variant="outlined"
                  disabled={disabled}
                />
                {!!selAdm1.hasChild && (
                  <AdmAutoComplete
                    label="Select Adm Area 2"
                    adm={adm2}
                    loading={loading}
                    onChange={handleAdm2Change}
                    style={{
                      margin: 5,
                      width: 300,
                      ...(selAdm2 && { display: "none" }),
                    }}
                    disabled={disabled}
                  />
                )}
                {selAdm2 && (
                  <Chip
                    label={selAdm2.name}
                    onDelete={() => onAdmDelete(2)}
                    style={{
                      margin: 5,
                      ...(selectedAdm && { backgroundColor: "#008000" }),
                    }}
                    variant="outlined"
                    disabled={disabled}
                  />
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </Box>
      <Divider />
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </Box>
  );
}
